"use client";

import { DataTable, Notice } from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useMoney } from "@/components/tools/finance/shared";
import type { FinancialRatioCalculatorStrings } from "@/components/tools/financial-ratio-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  RATIO_DEFS,
  RATIO_GROUPS,
  type RatioGroup,
  type RatioKey,
  type RatioOutput,
  type Unit,
} from "@/lib/tool-kit/accounting/ratios";

type Strings = FinancialRatioCalculatorStrings;

type NameKey = `name${Capitalize<RatioKey>}`;
type FormulaKey = `formula${Capitalize<RatioKey>}`;
type GroupKey = `group${Capitalize<RatioGroup>}`;
type NoteKey = `note${Capitalize<RatioGroup>}`;

function cap<T extends string>(value: T): Capitalize<T> {
  return (value.charAt(0).toUpperCase() + value.slice(1)) as Capitalize<T>;
}

export function RatioResults({
  output,
  currency,
  useAverage,
  days,
  strings,
}: {
  output: RatioOutput;
  currency: string;
  useAverage: boolean;
  days: number;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const money = useMoney(currency);
  const basis = useAverage ? strings.basisAverage : strings.basisEnding;
  const pct = (v: number, max = 2) =>
    `${fmt(v * 100, { maximumFractionDigits: max })}%`;
  const show = (value: number | null, unit: Unit): string => {
    if (value === null) {
      return strings.notAvailable;
    }
    if (unit === "percent") {
      return pct(value);
    }
    if (unit === "days") {
      return fmt(value, { maximumFractionDigits: 1 });
    }
    if (unit === "money") {
      return money(value);
    }
    return fmt(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const d = output.dupont;
  return (
    <section aria-live="polite" className="space-y-6">
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      {output.usedEndingFallback ? (
        <Notice>{strings.fallbackNote}</Notice>
      ) : null}
      {RATIO_GROUPS.map((group) => (
        <div className="space-y-2" key={group}>
          <h3 className={WIDGET_H3}>
            {strings[`group${cap(group)}` as GroupKey]}
          </h3>
          <p className="text-muted-foreground text-sm">
            {strings[`note${cap(group)}` as NoteKey]}
          </p>
          <DataTable
            head={[
              strings.ratioColumn,
              strings.formulaColumn,
              strings.valueColumn,
            ]}
            rows={RATIO_DEFS.filter((def) => def.group === group).map((def) => [
              strings[`name${cap(def.key)}` as NameKey],
              t(strings[`formula${cap(def.key)}` as FormulaKey], {
                basis,
                days: fmt(days),
              }),
              show(output.values[def.key], def.unit),
            ])}
          />
        </div>
      ))}
      {d ? (
        <div className={`${CARD} space-y-1`}>
          <h3 className={WIDGET_H3}>{strings.dupontHeading}</h3>
          <p className="font-mono text-muted-foreground text-sm leading-relaxed">
            {t(strings.dupontLine, {
              margin: pct(d.margin),
              turnover: fmt(d.turnover, { maximumFractionDigits: 4 }),
              multiplier: fmt(d.multiplier, { maximumFractionDigits: 4 }),
              roe: pct(d.roe),
            })}
          </p>
        </div>
      ) : null}
    </section>
  );
}
